import clsx from 'clsx';
import { useFm } from '@web3/base-hooks';
import { ActionButtons } from './ActionButtons';
import { useUserLotteryStore } from '@/store/userLotteryStatusStore';
import { useRedemptionWallet } from '@/hooks/wallet';
import { useEffect } from 'react';
import { LotteryFlag, UserRewardStatus } from '@/services/lottery';
import { Image } from '@bfe/moly';
import { RewardsListRulesButton } from './RewardsListRules';
import { thousandsSeparator, publicAssetsPath } from '@web3/base-utils';

const bgUrl = publicAssetsPath('/png/home/lottery-section-bg.png');

export const LotterySection = () => {
  const fm = useFm();
  const {
    isConnectedWallet,
    isBybitWallet,
    isPrivateKeyWallet,
    wallet: { walletId, walletType },
  } = useRedemptionWallet();
  const { data, isLoading, error, getUserRewardStatus } = useUserLotteryStore();

  useEffect(() => {
    getUserRewardStatus({ walletId, walletType });
  }, []);

  return (
    <div
      className={clsx('flex flex-col items-center pt-18 px-12')}
      style={{
        backgroundImage: `url(${bgUrl})`,
        backgroundRepeat: 'no-repeat',
        backgroundSize: '100% 100%',
      }}
    >
      <div className="headline headline-linear-color mb-2 text-center">
        {fm('unlockLotteryPrizePool')}
      </div>
      <div className="regular-20">
        {fm('obtainLotteryTicket', { amount: 1000 })}
      </div>
      <div className="grid grid-cols-2 py-10">
        <div className="flex items-center gap-4 border-e border-dashed border-[#404347] px-18">
          <FastTrackImage />
          <div>
            <div className="regular-14">{fm('totalTickets')}</div>
            <div className="h2">
              {getRewardsAmount({
                isConnectedWallet,
                isBybitWallet,
                isPrivateKeyWallet,
                isLoading,
                error,
                data,
              })}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-4 px-18">
          <FastTrackImage colored />
          <div>
            <div className="regular-14">{fm('remainingTickets')}</div>
            <div className="h2 main-linear-color">
              {getClaimableAmount({
                isConnectedWallet,
                isBybitWallet,
                isPrivateKeyWallet,
                isLoading,
                error,
                data,
              })}
            </div>
          </div>
        </div>
      </div>
      <LotteryPrizePoolStatusComment />
      <div className="flex gap-8 mt-8 mb-6">
        <ActionButtons />
      </div>
      {/* <RewardsListRulesButton /> */}
    </div>
  );
};

const FastTrackImage = ({ colored }: { colored?: boolean }) => {
  return (
    <Image
      src={
        colored
          ? 'https://s1.bycsi.com/bybit/deadpool/image-ffbc32e0321c4479b7c12bad25fcc038.png'
          : 'https://s1.bycsi.com/bybit/deadpool/image-9a2b839690294c5687acfab0c579edbd.png'
      }
      alt="Fast Track"
      width={64}
      height={64}
    />
  );
};

const getRewardsAmount = ({
  isConnectedWallet,
  isBybitWallet,
  isPrivateKeyWallet,
  isLoading,
  error,
  data,
}: {
  isConnectedWallet: boolean;
  isBybitWallet: boolean;
  isPrivateKeyWallet: boolean;
  isLoading: boolean;
  error: Error;
  data: UserRewardStatus;
}) => {
  if (!isConnectedWallet) {
    return '--';
  }

  if (!isBybitWallet) {
    return '--';
  }

  if (isPrivateKeyWallet) {
    return '0';
  }

  if (isLoading) {
    return '--';
  }

  if (error) {
    return '--';
  }

  return thousandsSeparator(data?.totalLotteryTimes);
};

const getClaimableAmount = ({
  isConnectedWallet,
  isBybitWallet,
  isPrivateKeyWallet,
  isLoading,
  error,
  data,
}: {
  isConnectedWallet: boolean;
  isBybitWallet: boolean;
  isPrivateKeyWallet: boolean;
  isLoading: boolean;
  error: Error;
  data: UserRewardStatus;
}) => {
  if (!isConnectedWallet) {
    return '--';
  }

  if (!isBybitWallet) {
    return '--';
  }

  if (isPrivateKeyWallet) {
    return '0';
  }

  if (isLoading) {
    return '--';
  }

  if (error) {
    return '--';
  }

  return thousandsSeparator(data?.remainingLotteryTimes);
};

const LotteryPrizePoolStatusComment = () => {
  const fm = useFm();
  const { data, isLoading, error } = useUserLotteryStore();

  if (isLoading) {
    return null;
  }

  if (error) {
    return null;
  }

  if (data?.lotteryFlag === LotteryFlag.NotEnoughStock) {
    return (
      <div className="regular-16 text-center">{fm('allRewardsFromPool')}</div>
    );
  }

  if (data?.lotteryFlag === LotteryFlag.Calculating) {
    return (
      <div className="regular-16 text-center">
        {fm('prizePoolCalcInProgress')}
      </div>
    );
  }

  return null;
};
